import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { VStack } from '@components/base/layout';
import { Spinner, Alert } from '@components/base/feedback';
import {
  documentsService,
  type VeosDocumentRow,
  type VeosClassementOption,
  type VeosMail,
  type VeosMailAttachment,
} from '../../api/documentsService';
import { EmailViewConfigurable, type EmailViewModel } from './EmailView';
import { EmailClassementFormConfigurable } from './DocumentClassementForm';
import { getDocumentKind, formatEmailDate, isReplyableEmail } from './documentHelpers';
import { parseReceivedEmail } from './emailParser';

interface EmailDetailProps {
  row: VeosDocumentRow;
  classementOptions: VeosClassementOption[];
  documents: VeosDocumentRow[];
  idSin: string;
  onGedded: () => void;
  /**
   * "Répondre" handler (mahhp 22227), called with this row. Only wired into the
   * view for a received GED e-mail (`isReplyableEmail`): an e-mail sent from
   * VEOS (`API === 'EMAIL'`) gets no button even when the host passes it.
   */
  onReply?: (row: VeosDocumentRow) => void;
}

const LOAD_ERROR = "Erreur lors du chargement de l'e-mail.";

function formatRecipients(list: VeosMail['dest']): string {
  if (!list || list.length === 0) return '';
  return list
    .map((r) => r.label || r.mail || '')
    .filter(Boolean)
    .join(', ');
}

function formatSender(exp: VeosMail['exp']): string {
  if (!exp) return '';
  if (exp.nom && exp.mail) return `${exp.nom} <${exp.mail}>`;
  return exp.nom || exp.mail || '';
}

/** True when a sent email's `corps` looks like HTML markup (vs legacy plain text). */
function looksLikeHtml(body: string): boolean {
  return /<[a-z][^>]*>/i.test(body);
}

export function EmailDetail({
  row,
  classementOptions,
  documents,
  idSin,
  onGedded,
  onReply,
}: Readonly<EmailDetailProps>) {
  const [email, setEmail] = useState<EmailViewModel | null>(null);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // For sent emails, attachment content is fetched lazily via `mail-pj-get`;
  // cache resolved base64 by attachment id so re-opening doesn't refetch. The
  // card remounts this component per selected row (`key={ID_ADOC}`), so the
  // refs and state always start fresh.
  const mailIdRef = useRef<string | number>(row.ID_ADOC);
  const attachmentCache = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const cache = attachmentCache.current;
    const isSent = row.API === 'EMAIL';

    const load = async () => {
      if (isSent) {
        const mail = await documentsService.fetchSentEmail(row.ID_ADOC);
        const attachments: VeosMailAttachment[] = mail.pjs ?? [];
        const body = mail.corps ?? '';
        const model: EmailViewModel = {
          from: formatSender(mail.exp),
          to: formatRecipients(mail.dest),
          date: formatEmailDate(mail.dtcre ?? mail.dateEnvoi),
          subject: mail.sujet ?? '',
          body,
          // VEOS round-trips the HTML EmailCard posts to `mail-send`, but legacy
          // or other-channel sent mails may have plain-text `corps`; sniff so
          // those keep rendering through `EmailView`'s plain-text branch.
          bodyIsHtml: looksLikeHtml(body) || undefined,
          attachments,
        };
        if (!cancelled) {
          setEmail(model);
          setLoadError(null);
        }
        return;
      }

      // Received email: fetch the document content, then best-effort parse.
      const content = await documentsService.fetchDocumentContent(row.ID_ADOC);
      const parsed = await parseReceivedEmail(
        content.datas,
        content.contentType ?? row.CONTENT_TYPE,
        content.name ?? row.NOM_ADOC ?? '',
        row.LIB_ADOC,
      );
      if (parsed.email.date) {
        parsed.email.date = formatEmailDate(parsed.email.date);
      }
      // The single fallback attachment is the original file — cache its bytes
      // so the open/GED actions don't need another round-trip.
      for (const att of parsed.email.attachments) {
        if (att.data) cache.set(String(att.id), att.data);
      }
      if (!cancelled) {
        setEmail(parsed.email);
        setNotice(parsed.notice);
        setLoadError(null);
      }
    };

    void load()
      .catch(() => {
        if (!cancelled) setLoadError(LOAD_ERROR);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [row.ID_ADOC, row.API, row.CONTENT_TYPE, row.NOM_ADOC, row.LIB_ADOC]);

  const resolveAttachment = useCallback(
    async (attachment: VeosMailAttachment): Promise<string> => {
      const key = String(attachment.id);
      const cached = attachmentCache.current.get(key);
      if (cached) return cached;
      if (attachment.data) {
        attachmentCache.current.set(key, attachment.data);
        return attachment.data;
      }
      // Sent-email attachment — fetch its content on demand.
      const resolved = await documentsService.fetchSentEmailAttachment(
        mailIdRef.current,
        attachment.id,
      );
      attachmentCache.current.set(key, resolved.data);
      return resolved.data;
    },
    [],
  );

  const kind = useMemo(() => getDocumentKind(row), [row]);

  const handleReply = useMemo(
    () =>
      onReply && isReplyableEmail(row)
        ? () => {
            onReply(row);
          }
        : undefined,
    [onReply, row],
  );

  if (kind !== 'email') return null;

  // The Classement + Libellé rename form (mahhp 22268: emails were previously
  // read-only) renders immediately — it only needs the row, not the fetched
  // email body — above whatever the content area is currently showing.
  return (
    <VStack gap={4}>
      <EmailClassementFormConfigurable
        row={row}
        classementOptions={classementOptions}
        onSaved={onGedded}
      />

      {isLoading && (
        <div className="flex items-center justify-center min-h-48">
          <Spinner size="md" />
        </div>
      )}

      {!isLoading && loadError && <Alert variant="error">{loadError}</Alert>}

      {!isLoading && !loadError && email && (
        <EmailViewConfigurable
          email={email}
          classementOptions={classementOptions}
          documents={documents}
          idSin={idSin}
          resolveAttachment={resolveAttachment}
          onGedded={onGedded}
          onReply={handleReply}
          notice={notice}
        />
      )}
    </VStack>
  );
}
